import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, RootDispatch } from "../../../store";
import { toast } from "react-hot-toast";
import Select from "react-select";
import { outwardProducts } from "../../../store/slices/Factory/newFloorSlice";
import { fetchProducts } from "../../../store/slices/productSlice";

interface UnitEntry {
  serialNumber: string;
  loggerSerialNumber: string;
  manufacturingSerialNumber: string;
  productId: string;
}

interface OutwardProductModalProps {
  isOpen: boolean;
  onClose: (success: boolean) => void;
  defaultRequestId: string;
  requestNumber: string;
  /** Optional pre-filled product ID */
  defaultProductId?: string;
}

const NewOutwardProductModal: React.FC<OutwardProductModalProps> = ({
  isOpen,
  onClose,
  requestNumber,
  defaultRequestId,
  defaultProductId = "",
}) => {
  const dispatch: RootDispatch = useDispatch();

  // Selectors
  const products = useSelector((state: RootState) => state.product.products);
  const isSubmitting = useSelector(
    (state: RootState) =>
      (state as any).floorInward?.loading?.outwardProductsloading,
  );

  useEffect(() => {
    if (!products || products.length <= 0) dispatch(fetchProducts());
    // dispatch(fetchDropdownData("factory"));
  }, [dispatch, products]);
  // Form level state
  const [requestId, setRequestId] = useState<string>(defaultRequestId);
  // Default product applied to new/unset units; each unit remains individually changeable
  const [targetProductId, setTargetProductId] =
    useState<string>(defaultProductId);

  // Unit rows state
  const [units, setUnits] = useState<UnitEntry[]>([
    {
      serialNumber: "",
      loggerSerialNumber: "",
      manufacturingSerialNumber: "",
      productId: defaultProductId,
    },
  ]);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const rowRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (defaultRequestId) setRequestId(defaultRequestId);
  }, [defaultRequestId]);

  if (!isOpen) return null;

  const isDuplicate = (
    field: keyof UnitEntry,
    value: string,
    currentIndex: number,
  ) => {
    if (!value) return false;
    return units.some(
      (unit, idx) => idx !== currentIndex && unit[field] === value,
    );
  };

  const addUnitRow = () => {
    const lastIndex = units.length - 1;
    const lastUnit = units[lastIndex];

    if (lastUnit) {
      if (!lastUnit.productId) {
        toast.error(`Unit #${lastIndex + 1}: Please select a Product.`);
        return;
      }
      if (!lastUnit.serialNumber || !/^\d{10}$/.test(lastUnit.serialNumber)) {
        toast.error(
          `Unit #${lastIndex + 1}: Valid 10-digit Serial Number required.`,
        );
        inputRefs.current[`serial-${lastIndex}`]?.focus();
        return;
      }
      if (!lastUnit.loggerSerialNumber.trim()) {
        toast.error(
          `Unit #${lastIndex + 1}: Logger Serial Number is required.`,
        );
        inputRefs.current[`logger-${lastIndex}`]?.focus();
        return;
      }
    }

    setUnits((prev) => [
      ...prev,
      {
        serialNumber: "",
        loggerSerialNumber: "",
        manufacturingSerialNumber: "",
        productId: targetProductId,
      },
    ]);

    setTimeout(() => {
      const newIndex = units.length;
      rowRefs.current[newIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      inputRefs.current[`serial-${newIndex}`]?.focus();
    }, 100);
  };

  const updateUnitRow = (
    index: number,
    field: keyof UnitEntry,
    value: string,
  ) => {
    setUnits((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleTargetProductChange = (value: string) => {
    setTargetProductId(value);
    setUnits((prev) => prev.map((u) => ({ ...u, productId: value })));
  };

  const removeUnitRow = (index: number) => {
    if (units.length === 1) {
      toast.error("At least one unit is required.");
      return;
    }
    setUnits((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
    field: keyof UnitEntry,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const value = (e.target as HTMLInputElement).value.trim();

      if (value && isDuplicate(field, value, index)) {
        toast.error(`Duplicate ${field} detected at Unit #${index + 1}`);
        updateUnitRow(index, field, "");
        return;
      }

      if (field === "serialNumber") {
        inputRefs.current[`logger-${index}`]?.focus();
      } else if (field === "loggerSerialNumber") {
        inputRefs.current[`manufacturing-${index}`]?.focus();
      } else if (field === "manufacturingSerialNumber") {
        addUnitRow();
      }
    }
  };

  const handleSummaryClick = (index: number) => {
    const el = rowRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-blue-500", "ring-offset-2");
      setTimeout(
        () => el.classList.remove("ring-2", "ring-blue-500", "ring-offset-2"),
        1200,
      );
      inputRefs.current[`serial-${index}`]?.focus();
    }
  };

  const handleSubmit = async () => {
    if (!requestId) {
      toast.error("Please select a Floor Request");
      return;
    }

    for (let i = 0; i < units.length; i++) {
      const u = units[i];
      const num = i + 1;

      if (!u.productId) {
        toast.error(`Unit #${num}: Please select a Product`);
        handleSummaryClick(i);
        return;
      }

      if (!/^\d{10}$/.test(u.serialNumber)) {
        toast.error(`Unit #${num}: Serial Number must be exactly 10 digits`);
        handleSummaryClick(i);
        return;
      }

      if (!u.loggerSerialNumber.trim()) {
        toast.error(`Unit #${num}: Logger Serial Number is required`);
        handleSummaryClick(i);
        return;
      }

      if (
        u.manufacturingSerialNumber &&
        !/^[a-zA-Z0-9]+$/.test(u.manufacturingSerialNumber)
      ) {
        toast.error(
          `Unit #${num}: Manufacturing Serial Number must be alphanumeric`,
        );
        handleSummaryClick(i);
        return;
      }
    }

    try {
      const res = await dispatch(
        outwardProducts({
          requestId,
          units,
        }),
      ).unwrap();

      onClose(res.success ?? false);

      // Reset state
      setRequestId("");
      setUnits([
        {
          serialNumber: "",
          loggerSerialNumber: "",
          manufacturingSerialNumber: "",
          productId: targetProductId,
        },
      ]);
    } catch (err: any) {
      toast.error(err || "Failed to submit outward production units");
    }
  };

  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: "2.6rem",
      borderRadius: "0.5rem",
      borderColor: "#E2E8F0",
      boxShadow: "none",
      "&:hover": { borderColor: "#CBD5E1" },
    }),
    menu: (provided: any) => ({ ...provided, zIndex: 9999 }),
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[110] transition-opacity">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden border border-slate-100">
        {/* MODAL HEADER */}
        <div className="flex-none bg-slate-50 px-4 sm:px-6 py-4 flex justify-between items-center border-b border-slate-200">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-slate-800">
              Create Floor Outward Request
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Specify the target Floor Request, Product, and scan serial
              details.
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition-colors focus:outline-none"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* MODAL BODY */}
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
          {/* MAIN FORM AREA */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 bg-white custom-scrollbar">
            {/* TOP SELECTION CARDS (Request & Target Product) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200/80">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                  Batch Number
                </label>
                <div className="inline-flex items-center rounded-full bg-indigo-100 border border-indigo-300 px-4 py-1.5 text-indigo-700 font-bold text-sm shadow-sm">
                  #{requestNumber}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                  Target Product
                </label>
                <Select
                  options={products.map((p) => ({
                    value: p._id,
                    label: p.productName,
                  }))}
                  value={
                    targetProductId
                      ? {
                          value: targetProductId,
                          label:
                            products.find((p) => p._id === targetProductId)
                              ?.productName || targetProductId,
                        }
                      : null
                  }
                  onChange={(opt) =>
                    handleTargetProductChange(opt ? opt.value : "")
                  }
                  placeholder="Select Product..."
                  styles={selectStyles}
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Applies to all units below; each unit can still be changed
                  individually.
                </p>
              </div>
            </div>

            {/* UNITS SECTION */}
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <span>Production Units</span>
                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                    {units.length}
                  </span>
                </h3>
                <span className="text-xs text-slate-400">
                  Press Enter after scanning to move next
                </span>
              </div>

              {units.map((unit, index) => (
                <div
                  key={index}
                  ref={(el) => (rowRefs.current[index] = el)}
                  className="bg-slate-50/70 hover:bg-slate-50 p-4 rounded-xl border border-slate-200/90 shadow-sm transition-all duration-200 scroll-m-20"
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className="inline-flex items-center text-xs font-bold text-slate-600 bg-slate-200/60 px-2.5 py-1 rounded-md">
                      Unit #{index + 1}
                    </span>
                    {units.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeUnitRow(index)}
                        className="text-red-500 hover:text-red-700 text-xs font-medium inline-flex items-center gap-1 transition-colors"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Product *
                    </label>
                    <Select
                      options={products.map((p) => ({
                        value: p._id,
                        label: p.productName,
                      }))}
                      value={
                        unit.productId
                          ? {
                              value: unit.productId,
                              label:
                                products.find((p) => p._id === unit.productId)
                                  ?.productName || unit.productId,
                            }
                          : null
                      }
                      onChange={(opt) =>
                        updateUnitRow(index, "productId", opt ? opt.value : "")
                      }
                      placeholder="Select Product..."
                      styles={selectStyles}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Serial Number Input */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Serial Number *
                      </label>
                      <input
                        ref={(el) =>
                          (inputRefs.current[`serial-${index}`] = el)
                        }
                        type="text"
                        placeholder="10 Digits"
                        value={unit.serialNumber}
                        onChange={(e) =>
                          updateUnitRow(index, "serialNumber", e.target.value)
                        }
                        onKeyDown={(e) =>
                          handleKeyDown(e, index, "serialNumber")
                        }
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                        autoComplete="off"
                      />
                    </div>

                    {/* Logger Serial Input */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Logger Serial *
                      </label>
                      <input
                        ref={(el) =>
                          (inputRefs.current[`logger-${index}`] = el)
                        }
                        type="text"
                        placeholder="Scan Logger SN"
                        value={unit.loggerSerialNumber}
                        onChange={(e) =>
                          updateUnitRow(
                            index,
                            "loggerSerialNumber",
                            e.target.value,
                          )
                        }
                        onKeyDown={(e) =>
                          handleKeyDown(e, index, "loggerSerialNumber")
                        }
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                        autoComplete="off"
                      />
                    </div>

                    {/* Manufacturing Serial Input */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Manufacturing SN{" "}
                        <span className="text-slate-400 font-normal">
                          (Optional)
                        </span>
                      </label>
                      <input
                        ref={(el) =>
                          (inputRefs.current[`manufacturing-${index}`] = el)
                        }
                        type="text"
                        placeholder="Alphanumeric"
                        value={unit.manufacturingSerialNumber}
                        onChange={(e) =>
                          updateUnitRow(
                            index,
                            "manufacturingSerialNumber",
                            e.target.value,
                          )
                        }
                        onKeyDown={(e) =>
                          handleKeyDown(e, index, "manufacturingSerialNumber")
                        }
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addUnitRow}
                className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50/50 transition-all font-medium text-sm flex items-center justify-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Another Unit Entry
              </button>
            </div>
          </div>

          {/* RIGHT SIDE SUMMARY PANEL */}
          <div className="w-full lg:w-80 bg-slate-50 border-t lg:border-t-0 lg:border-l border-slate-200 flex flex-col flex-none max-h-56 lg:max-h-none">
            <div className="p-4 border-b border-slate-200 bg-slate-100/60">
              <h3 className="font-bold text-slate-800 text-sm">Summary</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {units.length} unit(s) pending dispatch
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
              {units.map((unit, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSummaryClick(idx)}
                  className="bg-white p-2.5 rounded-lg border border-slate-200 hover:border-blue-400 hover:shadow-sm transition-all cursor-pointer text-xs"
                >
                  <div className="flex justify-between items-center font-semibold text-slate-700 mb-1">
                    <span>Unit #{idx + 1}</span>
                    <span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
                      {unit.serialNumber || "No SN"}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 flex justify-between">
                    <span>Logger: {unit.loggerSerialNumber || "-"}</span>
                    <span>Mfg: {unit.manufacturingSerialNumber || "-"}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FOOTER ACTIONS */}
        <div className="flex-none p-4 sm:p-6 bg-white border-t border-slate-200 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <span>Submitting Units...</span>
              </>
            ) : (
              <span>Submit Outward Request</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="py-3 px-6 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewOutwardProductModal;
